import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import type { Deployment, DeployRequest } from '../types/api';

interface UseDeploymentsOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
  filters?: {
    status?: string;
    repoName?: string;
    limit?: number;
  };
}

interface UseDeploymentsReturn {
  deployments: Deployment[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  deployFeature: (request: DeployRequest) => Promise<Deployment>;
  cancelDeployment: (deploymentId: string) => Promise<void>;
  rollbackDeployment: (deploymentId: string) => Promise<void>;
}

export function useDeployments(options: UseDeploymentsOptions = {}): UseDeploymentsReturn {
  const { autoRefresh = false, refreshInterval = 10000, filters } = options;

  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDeployments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getDeployments(filters);
      setDeployments(data);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch deployments:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const refresh = useCallback(async () => {
    await fetchDeployments();
  }, [fetchDeployments]);

  const deployFeature = useCallback(async (request: DeployRequest): Promise<Deployment> => {
    try {
      const deployment = await apiService.deployFeature(request);
      await fetchDeployments();
      return deployment;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchDeployments]);

  const cancelDeployment = useCallback(async (deploymentId: string) => {
    try {
      await apiService.cancelDeployment(deploymentId);
      await fetchDeployments();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchDeployments]);

  const rollbackDeployment = useCallback(async (deploymentId: string) => {
    try {
      await apiService.rollbackDeployment(deploymentId);
      await fetchDeployments();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchDeployments]);

  useEffect(() => {
    fetchDeployments();
  }, [fetchDeployments]);

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(fetchDeployments, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchDeployments]);

  return {
    deployments,
    loading,
    error,
    refresh,
    deployFeature,
    cancelDeployment,
    rollbackDeployment,
  };
}
